// Import React for hooks
import React from 'react';

// Mobile Navigation Components
export { default as MobileNavigation } from './MobileNavigation';
export type { MobileNavItem, MobileNavigationProps } from './MobileNavigation';
export { getSchoolNavItems, getAdminNavItems, useMobileNavigation } from './MobileNavigation';

// Touch Gesture Components
export { default as TouchGestures } from './TouchGestures';
export type { TouchGestureConfig, TouchGestureProps } from './TouchGestures';
export { useSwipeToDelete, usePullToRefresh } from './TouchGestures';

// Responsive Table Components
export { default as ResponsiveTable } from './ResponsiveTable';
export type { 
  TableColumn, 
  TableAction, 
  SwipeAction, 
  ResponsiveTableProps 
} from './ResponsiveTable';

// Mobile Modal Components
export { default as MobileModal } from './MobileModal';
export type { MobileModalProps } from './MobileModal';
export { 
  useMobileModal, 
  BottomSheet, 
  FullScreenModal, 
  withMobileModal 
} from './MobileModal';

// Utility functions for mobile responsiveness
export const isMobileDevice = (): boolean => {
  return typeof window !== 'undefined' && window.innerWidth < 768;
};

export const isTouchDevice = (): boolean => {
  return typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
};

export const getViewportHeight = (): number => {
  if (typeof window === 'undefined') return 0;
  
  // Use dynamic viewport height if available, fallback to regular viewport height
  return window.visualViewport?.height || window.innerHeight;
};

export const getSafeAreaInsets = () => {
  if (typeof window === 'undefined') {
    return { top: 0, bottom: 0, left: 0, right: 0 };
  }

  const style = getComputedStyle(document.documentElement);
  
  return {
    top: parseInt(style.getPropertyValue('env(safe-area-inset-top)') || '0'),
    bottom: parseInt(style.getPropertyValue('env(safe-area-inset-bottom)') || '0'),
    left: parseInt(style.getPropertyValue('env(safe-area-inset-left)') || '0'),
    right: parseInt(style.getPropertyValue('env(safe-area-inset-right)') || '0'),
  };
};

// Mobile breakpoint constants
export const MOBILE_BREAKPOINTS = {
  xs: 375,   // Small mobile
  sm: 480,   // Large mobile
  md: 768,   // Tablet
  lg: 1024,  // Desktop
  xl: 1280,  // Large desktop
} as const;

export type MobileBreakpoint = keyof typeof MOBILE_BREAKPOINTS;

// Hook for responsive breakpoints
export const useBreakpoint = (breakpoint: MobileBreakpoint = 'md') => {
  const [isAbove, setIsAbove] = React.useState(false);

  React.useEffect(() => {
    const checkBreakpoint = () => {
      setIsAbove(window.innerWidth >= MOBILE_BREAKPOINTS[breakpoint]);
    };

    checkBreakpoint();
    window.addEventListener('resize', checkBreakpoint);
    return () => window.removeEventListener('resize', checkBreakpoint);
  }, [breakpoint]);

  return isAbove;
};

// Hook for detecting mobile orientation
export const useOrientation = () => {
  const [orientation, setOrientation] = React.useState<'portrait' | 'landscape'>('portrait');

  React.useEffect(() => {
    const checkOrientation = () => {
      setOrientation(window.innerHeight > window.innerWidth ? 'portrait' : 'landscape');
    };

    checkOrientation();
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);
    
    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, []);

  return orientation;
};